import { firstIncompleteProfileStep, type ProfileUser } from './applicant-form.ts'
import { firstIncompleteCompanyStep, type CompanyUser } from './company-form.ts'
import { effectiveCompanyRole, type EmployeeRole } from './shared.ts'

// The subset of the resolved user the post-login destination depends on. Satisfied by both the
// client's getUser output and the server's `User` (shared/types) — keep it structural so both pass.
export type RedirectUser = ProfileUser &
  CompanyUser & {
    type: 'company' | 'applicant'
    testPassed: boolean | null
    testPassedValues: boolean | null
    testPassedPersonality: boolean | null
    isEmployee?: boolean | null
    // Active-company context (set on the login response), so we can route admin/authorized employees
    // into the company view. `effectiveCompanyRole` reads these two fields.
    activeCompanyId?: number | null
    activeEmployer?: { employeeType?: EmployeeRole | null } | null
  }

export type PostLoginDestination =
  | {
      to:
        | '/report'
        | '/employees'
        | '/register/applicant'
        | '/register/company'
        | '/test/competences'
        | '/test/values'
        | '/test/personality'
    }
  | { href: string }

// Build an onboarding-gate href that threads the visitor's intended post-onboarding target through.
const withRedirect = (path: string, redirect: string): string => `${path}?redirect=${encodeURIComponent(redirect)}`

// Where a user should land right after authenticating. Single source of truth for both the client
// (password / social login) and the server OAuth callback.
//
//   - `redirect`    an explicit target the user was headed to. Honored ONLY once the user is fully
//                   registered: an unfinished user is routed to complete onboarding first, with the
//                   redirect carried along (in `search.redirect`) so it still applies at the end.
//                   (Honoring it immediately would strand a brand-new user — e.g. a fresh social
//                   sign-up — on a public, non-test-gated page like /market with no profile to show.)
export function resolvePostLoginPath(user: RedirectUser, opts: { redirect?: string } = {}): PostLoginDestination {
  const { redirect } = opts

  // ── Registration gates take precedence over the redirect: an unfinished user is routed to complete
  //    onboarding first, with the redirect carried along (search.redirect) so it still applies at the end. ──

  // An applicant must finish the TEST before any redirect is honored — without it there's no
  // psychographic profile (so a fresh social sign-up redirected to a public page like /market would
  // land with nothing to show). The profile wizard that follows the test is enforced by the
  // test→/register/applicant client flow, so test completion is the gate here. Resume at the first
  // pending questionnaire.
  if (user.type === 'applicant' && (!user.testPassed || !user.testPassedValues || !user.testPassedPersonality)) {
    const to = !user.testPassed ? '/test/competences' : !user.testPassedValues ? '/test/values' : '/test/personality'
    return redirect ? { href: withRedirect(to, redirect) } : { to }
  }

  // A half-registered company must finish its wizard (it has no test of its own) before a redirect is
  // honored, instead of silently landing on Employees (which let companies skip required fields).
  if (user.type === 'company') {
    const incompleteStep = firstIncompleteCompanyStep(user)
    if (incompleteStep)
      return redirect ? { href: withRedirect('/register/company', redirect) } : { to: '/register/company' }
    return redirect ? { href: redirect } : { to: '/employees' }
  }

  // ── Registered enough (test done): honor an explicit deep-link / compare target (opaque href). ──
  if (redirect) return { href: redirect }

  // ── Admin/authorized employees default into the COMPANY view, landing on Employees (they manage the
  //    company). This mirrors the session's interfaceMode='company' set for a corporate-email login. A
  //    collaborator (role neither administrator nor authorized) or a self-registered professional falls
  //    through to their own profile below. Role is null unless an active company context is set (i.e. a
  //    corporate-email login), so a personal-email login of an admin still lands on their profile. ──
  const role = effectiveCompanyRole(user)
  if (user.isEmployee && (role === 'administrator' || role === 'authorized')) return { to: '/employees' }

  // ── No redirect: an applicant ALWAYS lands on their own profile ("Mi perfil"), regardless of how
  //    they registered. A self-registered professional resumes the profile wizard while it's still
  //    incomplete; a company-invited employee skips the wizard (their profile data comes from the
  //    company) and lands directly on the report. Neither lands on /jobs — that section is for
  //    companies (managing positions) and is reached from the nav, never as a default destination. ──
  const incompleteStep = user.isEmployee ? null : firstIncompleteProfileStep(user)
  return incompleteStep ? { to: '/register/applicant' } : { to: '/report' }
}

// Serialize a destination to a URL string for the server's HTTP redirect (the client uses the navigate
// options directly). `{ href }` is already a built URL; `{ to }` is a bare route path.
export function postLoginHref(dest: PostLoginDestination): string {
  return 'href' in dest ? dest.href : dest.to
}
